import { useCallback, useContext, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { StoreContext } from '../../store';
import useTranslation from 'next-translate/useTranslation';
import { Section } from '@microrealestate/commonui/components';
import { Button, Grid, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useConfirmDialog from '../ConfirmDialog';
import { useRouter } from 'next/router';

const useStyles = makeStyles((theme) => ({
  sectionWithMargin: {
    marginTop: '50px',
  },
  dangerZone: {
    padding: '16px',
    border: '1px solid',
    borderColor: theme.palette.danger.dark,
    borderRadius: '8px',
    color: theme.palette.danger.dark
  }
}));

const ResetSettings = observer(() => {
  const store = useContext(StoreContext);
  const { t } = useTranslation('common');
  const classes = useStyles();
  const [ConfirmDeleteDialog, setOpenConfirmDialog] = useConfirmDialog();
  const router = useRouter();

  const [actionType, setActionType] = useState(null);

  const handleDeleteTenant = useCallback(() => {
    setActionType('deleteTenant');
    setOpenConfirmDialog(true);
  }, [setOpenConfirmDialog]);

  const handleResetLandlord = useCallback(() => {
    setActionType('resetLandlord');
    setOpenConfirmDialog(true);
  }, [setOpenConfirmDialog]);

  const onDeleteTenant = useCallback(async () => {
    try {
      const { status, message } = await store.tenant.deleteAll();

      if (status === 200) {
        store.pushToastMessage({
          message: message,
          severity: 'success',
        });
      } else {
        store.pushToastMessage({
          message: t('Error delete tenant'),
          severity: 'error',
        });
      }
    } catch (error) {
      console.error(error);
      store.pushToastMessage({
        message: t('Something went wrong'),
        severity: 'error',
      });
    }
    await router.push(
      `/settings`
    );
  }, [store, t]);

  const onResetLandlord = useCallback(async () => {
    try {
      const id = store.organization.selected._id;
      const { status } = await store.organization.reset(id);
      if (status === 200) {
        store.pushToastMessage({
          message: t('Successfully reset data'),
          severity: 'success',
        });
      } else {
        store.pushToastMessage({
          message: t('Error reset data'),
          severity: 'error',
        });
      }
    } catch (error) {
      console.error(error);
      store.pushToastMessage({
        message: t('Something went wrong'),
        severity: 'error',
      });
    }
    await router.push(
      `/settings`
    );
  }, [store, t]);

  const handleConfirm = useCallback(() => {
    if (actionType === 'deleteTenant') {
      onDeleteTenant();
    } else if (actionType === 'resetLandlord') {
      onResetLandlord();
    }
  }, [actionType, onDeleteTenant, onResetLandlord]);

  return (
    <Box className={classes.sectionWithMargin}>
      <Section label={t('Reset Data')} className={classes.dangerZone}>
        <Grid container spacing={2}>
          <Grid item container xs={12} sm={6} justifyContent='space-between' alignItems='center'>
            <Grid item >
              <Typography variant="body2" color="textSecondary">
                {t("Delete all tenant's data")}
              </Typography>
            </Grid>
            <Grid item >
              <Button size="small" variant="contained" color="secondary" onClick={handleDeleteTenant} className='shepherd-setting-removeTenants'>
                {t('Delete')}
              </Button>
            </Grid>
          </Grid>
          <Grid item container xs={12} sm={6} justifyContent='space-between' alignItems='center'>
            <Grid item >
              <Typography variant="body2" color="textSecondary">
                {t('Delete all tenant, room, and property data')}
              </Typography>
            </Grid>
            <Grid item >
              <Button size="small" variant="contained" color="secondary" onClick={handleResetLandlord} className='shepherd-setting-resetData'>
                {t('Reset')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <ConfirmDeleteDialog
          title={
            actionType === 'deleteTenant'
              ? t('Are you sure you want to delete all tenants?')
              : t('Are you sure you want to reset landlord data?')
          }
          subTitle={
            actionType === 'deleteTenant'
              ? t('This action will permanently remove all tenant data. This cannot be undone.')
              : t('This action will reset landlord data except subscription. This cannot be undone.')
          }
          onConfirm={handleConfirm}
        />
      </Section>
    </Box>
  );
});

export default ResetSettings;
